import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery } from '@tanstack/react-query'

import { FlexCenter } from '../../lib/components'
import { OpportunityListV2Presenter } from './opportunity-list-v2-presenter'
import { RawOpportunity, SearchParams } from '../../lib/types'
import { opportunityToCardProps } from '../home-v2/format-opportunity'
import type { InvestmentCardV2Props } from '../home-v2/investment-card-v2'
import { comingSoonServices, projectServices, kycServices } from '../../services'
import type { ComingSoonPublicItem } from '../../services/coming-soon-service'
import { RootState } from '../../store'
import { showBanner } from '../../global-state/banner-slice'
import { setPreviousUrl } from '../../lib/utils/helpers'
import {
  buildCampaignSignupRedirectFromOpportunity,
  storeCampaignSignupRedirect,
} from '../../lib/utils/campaign-signup-redirect'
import { updateConfiguration } from '../../global-state/investor-slice'
import { logCrmEvent, EngagementEvents } from '../../lib/utils/crmEvents'
import { shouldPromptConfidentialityAgreement } from '../../lib/utils/onboarding'

const DEFAULT_LIMIT = 8
const INITIAL_SEARCH_PARAMS: Required<
  SearchParams & { minInvestment?: string; maxInvestment?: string; jurisdiction?: string }
> = {
  limit: String(DEFAULT_LIMIT),
  offset: '0',
  sortBy: 'DISPLAY_ORDER',
  keyword: '',
  filterBy: '',
  status: 'PUBLISHED',
  minInvestment: '',
  maxInvestment: '',
  jurisdiction: '',
}

function OpportunityListV2Container() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { authToken, isEmailVerified, acceptedNDA, riskProfileCompleted, userId } = useSelector(
    (state: RootState) => state.investor,
  )
  const [searchParams, setSearchParams] = useState(INITIAL_SEARCH_PARAMS)
  const [pagination, setPagination] = useState({
    total: 0,
    limit: DEFAULT_LIMIT,
    count: 0,
    offset: 0,
  })
  const [accumulatedProjects, setAccumulatedProjects] = useState<RawOpportunity[]>([])
  const hasLoggedVisitRef = useRef(false)

  const { data: assetClassesData } = useQuery({
    queryKey: ['assetClasses'],
    queryFn: () => projectServices.getAssetClasses(),
  })

  const {
    data: comingSoonData,
    isLoading: comingSoonIsLoading,
    isFetching: comingSoonIsFetching,
  } = useQuery({
    queryKey: ['coming-soon-opportunities', 'public'],
    queryFn: async () => {
      try {
        return await comingSoonServices.getPublicList()
      } catch {
        return { items: [] as ComingSoonPublicItem[] }
      }
    },
  })

  const assetClasses = useMemo(() => {
    if (assetClassesData?.data?.data) {
      return assetClassesData.data.data as string[]
    }
    return []
  }, [assetClassesData?.data?.data])

  const buildParams = (): SearchParams => {
    const params: SearchParams = {
      limit: searchParams.limit,
      offset: searchParams.offset,
      sortBy: (searchParams.sortBy || 'DISPLAY_ORDER') as SearchParams['sortBy'],
      keyword: searchParams.keyword,
      filterBy: searchParams.filterBy,
      status: searchParams.status,
    }
    // UI stores USD values; API filter compares stored ticket_size (cents).
    if (searchParams.minInvestment) {
      params.minInvestment = String(Math.round(Number(searchParams.minInvestment) * 100))
    }
    if (searchParams.maxInvestment) {
      params.maxInvestment = String(Math.round(Number(searchParams.maxInvestment) * 100))
    }
    if (searchParams.jurisdiction) params.jurisdiction = searchParams.jurisdiction
    return params
  }

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['opportunities-v2', searchParams],
    queryFn: async () => {
      return projectServices.getSearchOpportunities(buildParams(), {
        Authorization: authToken ?? '',
      })
    },
  })

  const handleSearchParams = useCallback((updates: Partial<typeof INITIAL_SEARCH_PARAMS>) => {
    const isLoadMore = 'offset' in updates
    setSearchParams((prev) => ({
      ...prev,
      ...updates,
      ...(isLoadMore ? {} : { offset: '0' }),
    }))
    if (!isLoadMore) {
      setPagination((prev) => ({ ...prev, offset: 0 }))
    }
  }, [])

  const handleLoadMore = useCallback(() => {
    setPagination((prev) => {
      const nextOffset = prev.offset + prev.limit
      setSearchParams((prevParams) => ({ ...prevParams, offset: String(nextOffset) }))
      return { ...prev, offset: nextOffset }
    })
  }, [])

  const handleProjectClick = useCallback(
    (projectSlug: string, id: number, title?: string) => {
      if (authToken && isEmailVerified) {
        if (shouldPromptConfidentialityAgreement(riskProfileCompleted, acceptedNDA)) {
          setPreviousUrl(`/opportunity/${projectSlug}/${id}`)
        } else {
          setPreviousUrl('')
        }
        navigate(`/opportunity/${projectSlug}/${id}`)
      } else if (authToken && !isEmailVerified) {
        dispatch(
          showBanner({
            text: 'Please verify your email address to view the opportunity details.',
            variant: 'warning-banner',
          }),
        )
      } else {
        storeCampaignSignupRedirect(
          buildCampaignSignupRedirectFromOpportunity(projectSlug, id, title),
        )
        navigate('/login')
      }
    },
    [authToken, isEmailVerified, acceptedNDA, riskProfileCompleted, navigate, dispatch],
  )

  const resetSearchParams = useCallback(() => {
    setSearchParams(INITIAL_SEARCH_PARAMS)
    setPagination((prev) => ({ ...prev, offset: 0 }))
  }, [])

  useEffect(() => {
    if (hasLoggedVisitRef.current) return
    hasLoggedVisitRef.current = true
    logCrmEvent(EngagementEvents.VISITED_OPPORTUNITY, { Authorization: authToken ?? '' })
  }, [authToken])

  useEffect(() => {
    if (!data) return
    if (data.pagination) setPagination(data.pagination)
    const pageOffset = data.pagination?.offset ?? 0
    const opportunities = data.opportunities ?? []
    if (pageOffset === 0) {
      setAccumulatedProjects(opportunities)
    } else {
      setAccumulatedProjects((prev) => {
        const seenIds = new Set(prev.map((o) => o.id))
        const unique = opportunities.filter((o: RawOpportunity) => !seenIds.has(o.id))
        return [...prev, ...unique]
      })
    }
  }, [data])

  const isLoadingMore = isFetching && Number(searchParams.offset) > 0
  const isFiltering =
    isFetching && Number(searchParams.offset) === 0 && accumulatedProjects.length > 0
  const isInitialLoading = isLoading && accumulatedProjects.length === 0

  const opportunityCards: Omit<InvestmentCardV2Props, 'onCtaClick'>[] = useMemo(
    () =>
      accumulatedProjects.map((opp) => {
        const props = opportunityToCardProps(
          opp,
          () => handleProjectClick(opp.opportunity_slug, opp.id, opp.opportunity_title),
          { alwaysLearnMoreCta: true, enableHighlight: true },
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, react/prop-types -- onCtaClick omitted here, injected in presenter
        const { onCtaClick, ...rest } = props
        return rest
      }),
    [accumulatedProjects, handleProjectClick],
  )

  useEffect(() => {
    if (!authToken || !userId) return
    let cancelled = false
    const getKYC = async () => {
      try {
        const response = await kycServices.getKYC_v1(userId.toString(), {
          Authorization: authToken,
        })
        if (cancelled) return
        if (response?.data && response.status === 200) {
          if (response.data.status) {
            dispatch(updateConfiguration({ name: 'kycStatus', value: response.data.status }))
          }
          if (response.data.is_nda_accepted !== undefined) {
            dispatch(
              updateConfiguration({
                name: 'acceptedNDA',
                value: response.data.is_nda_accepted,
              }),
            )
          }
        }
      } catch {
        // KYC fetch failed silently; will be retried on next mount
      }
    }
    getKYC()
    return () => {
      cancelled = true
    }
  }, [authToken, userId, dispatch])

  const hasMore = pagination.offset + pagination.count < pagination.total

  return (
    <FlexCenter>
      <OpportunityListV2Presenter
        pagination={pagination}
        searchParams={searchParams}
        handleSearchParams={handleSearchParams}
        opportunityCards={opportunityCards}
        opportunityLoading={isInitialLoading}
        isFiltering={isFiltering}
        isLoadingMore={isLoadingMore}
        onOpportunityCtaClick={handleProjectClick}
        handleLoadMore={handleLoadMore}
        resetSearchParams={resetSearchParams}
        assetClasses={assetClasses}
        hasMore={hasMore}
        comingSoonItems={comingSoonData?.items ?? []}
        comingSoonLoading={comingSoonIsLoading || comingSoonIsFetching}
      />
    </FlexCenter>
  )
}

export { OpportunityListV2Container }
